import { computed, ref } from 'vue'
import VisitService from '@/services/visit.service'
import { roundToTwoDecimals } from '@/services/number.service'

export function useDiscountField({ scope, service, teeth, predefinedType, versionCount }) {

  const serviceInstances = computed(() => {
    if (scope === 'allServicesOfGroup') {
      return teeth.value.flatMap(tooth => tooth.services)
    }
    return teeth.value.flatMap(tooth =>
      tooth.services.filter(s => s.id === service.value?.id)
    )
  })

  const discountType = computed(() => {
    if(predefinedType.value) return predefinedType.value
    if (scope === 'allServicesOfGroup') return 'percent'
    return service.value?.pivot?.discount_type || 'percent'
  })

  const canChangeType = !predefinedType.value && ['serviceOfGroup', 'serviceOfAllTeeth'].includes(scope)

  const totalOriginalPrice = computed(() =>
    serviceInstances.value.reduce((sum, s) => sum + Number(s.pivot.price_original || 0), 0)
  )

  const totalDiscountAmount = computed(() =>
    serviceInstances.value.reduce((sum, s) => sum + VisitService.calculateDiscountOfService(s), 0)
  )

  const displayValue = computed(() => {
    if (scope === 'allServicesOfGroup') {
      if (!serviceInstances.value.length) return ''
      const first = serviceInstances.value[0].pivot.discount
      const allSame = serviceInstances.value.every(
        s => s.pivot.discount_type === 'percent' && s.pivot.discount === first
      )
      return allSame ? (first || 0) : ''
    }
    if (predefinedType.value === 'absolute') {
      return roundToTwoDecimals(totalDiscountAmount.value)
    }
    if (predefinedType.value === 'percent') {
      if (!totalOriginalPrice.value) return 0
      return roundToTwoDecimals((totalDiscountAmount.value / totalOriginalPrice.value) * 100)
    }
    if (discountType.value === 'absolute') {
      return roundToTwoDecimals(serviceInstances.value.reduce((sum, s) => sum + Number(s.pivot.discount_absolute || 0), 0))
    }
    if (scope === 'serviceOfAllTeeth') {
      if (!totalOriginalPrice.value) return 0
      return roundToTwoDecimals((totalDiscountAmount.value / totalOriginalPrice.value) * 100)
    }
    return service.value?.pivot?.discount || 0
  })

  const editingValue = ref(null)
  const editingType = ref(false)
  const justOpened = ref(false)

  const isEditingValue = computed(() => editingValue.value !== null)

  const _flagJustOpened = () => {
    justOpened.value = true
    setTimeout(() => { justOpened.value = false }, 100)
  }

  const openValueEdit = () => {
    editingValue.value = displayValue.value
    editingType.value = false
    _flagJustOpened()
  }

  const openTypeEdit = () => {
    editingType.value = true
    editingValue.value = null
    _flagJustOpened()
  }

  const cancelValueEdit = () => {
    editingValue.value = null
  }

  const closeValueEdit = () => {
    if (justOpened.value) return
    commitValueEdit()
  }

  const closeTypeEdit = () => {
    if (justOpened.value) return
    editingType.value = false
  }

  const clampValue = (val) => {
    if (val < 0) return 0
    if (discountType.value === 'percent' && val > 100) return 100
    if (discountType.value === 'absolute' && val > totalOriginalPrice.value) return totalOriginalPrice.value
    return val
  }

  const onKeyup = () => {
    if (editingValue.value === null) return
    const num = editingValue.value === '' ? 0 : Number(editingValue.value)
    const clamped = clampValue(num)
    if (clamped !== num) editingValue.value = clamped
    versionCount.value++
  }

  const commitValueEdit = () => {
    const raw = editingValue.value
    editingValue.value = null
    if (raw === null) return
    const val = clampValue(raw === '' ? 0 : Number(raw))
    if (val === displayValue.value) return
    _applyDiscount(val)
    versionCount.value++
  }

  const _applyDiscount = (val) => {
    if (scope === 'allServicesOfGroup') {
      serviceInstances.value.forEach(s => {
        s.pivot.discount = val
        s.pivot.discount_type = 'percent'
      })
      return
    }
    if (discountType.value === 'percent') {
      serviceInstances.value.forEach(s => {
        s.pivot.discount = val
        s.pivot.discount_type = 'percent'
      })
    } else {
      const instances = serviceInstances.value
      const count = instances.length || 1
      const perTooth = roundToTwoDecimals(val / count)
      const lastTooth = roundToTwoDecimals(val - perTooth * (count - 1))
      instances.forEach((s, i) => {
        const isLast = i === (count - 1)
        s.pivot.discount_absolute = isLast ? lastTooth : perTooth
        s.pivot.discount_type = 'absolute'
      })
    }
  }

  const changeType = (newType) => {
    editingType.value = false
    if (!canChangeType || newType === discountType.value) return

    const priceOriginal = Number(service.value?.pivot?.price_original || 0)
    const count = serviceInstances.value.length || 1

    if (newType === 'absolute') {
      const pct = typeof displayValue.value === 'number' ? displayValue.value : 0
      const absolutePerTooth = roundToTwoDecimals((pct / 100) * priceOriginal)
      serviceInstances.value.forEach(s => {
        s.pivot.discount_type = 'absolute'
        s.pivot.discount_absolute = absolutePerTooth
      })
    } else {
      const totalAbs = serviceInstances.value.reduce((sum, s) => sum + Number(s.pivot.discount_absolute || 0), 0)
      const percentVal = priceOriginal > 0
        ? roundToTwoDecimals((totalAbs / count / priceOriginal) * 100)
        : 0
      serviceInstances.value.forEach(s => {
        s.pivot.discount_type = 'percent'
        s.pivot.discount = percentVal
        s.pivot.discount_absolute = 0
      })
    }
    versionCount.value++
  }

  return {
    discountType,
    canChangeType,
    displayValue,
    editingValue,
    isEditingValue,
    editingType,
    openValueEdit,
    openTypeEdit,
    cancelValueEdit,
    closeValueEdit,
    closeTypeEdit,
    onKeyup,
    changeType,
  }
}
